import React from 'react';
import {
    Calendar,
    MapPin,
    Users,
    Phone,
    User,
    Shield,
    Layers, // Added for sub-events icon
} from 'lucide-react';

const EventHeader = ({ event, isGroupEvent, maxMembers, hasSubEvents }) => {
    // Function to determine font size based on event name length
    const getEventNameFontSize = (name) => {
        if (!name) return 'text-3xl';
        
        const length = name.length;
        if (length > 50) return 'text-xl md:text-2xl';
        if (length > 40) return 'text-2xl md:text-2xl';
        if (length > 30) return 'text-2xl md:text-3xl';
        if (length > 20) return 'text-2xl md:text-3xl';
        return 'text-xl md:text-3xl';
    };
    return (
        <div className="relative gap-2 bg-white border border-gray-100 shadow-2xl rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-3xl group/header">
            {/* Background Decorative Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-50/40 via-transparent to-transparent rounded-full -translate-y-48 translate-x-48"></div>
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-amber-50/30 via-transparent to-transparent rounded-full -translate-x-40 translate-y-40"></div>
                <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-r from-emerald-50/20 via-transparent to-transparent rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            </div>

            {/* Top Banner Strip */}
            <div className="relative bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 px-6 py-3">
                <div className="flex items-center justify-between">
                    {/* Left section - Logo & College */}
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-16 h-12 flex items-center justify-center bg-white backdrop-blur-md rounded-xl border border-white/20">
                                <img
                                    src="/EMS/Images/Logo.png"
                                    alt="PSG Logo"
                                    className="w-14 h-14 object-contain"
                                />
                            </div>
                            <div className="text-white">
                                <div className="text-sm font-semibold leading-tight">
                                    Centre for Non-Formal & Continuing Education (CNCE)
                                </div>
                                <div className="text-xs font-medium text-blue-200">
                                    {event.INST_NAME}
                                </div>
                            </div>
                            <div className="hidden md:flex items-center">
                                <div className="relative px-4 py-2">
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 blur-xl opacity-50"></div>
                                    <span className="relative px-6 text-lg font-bold text-white bg-gradient-to-r from-blue-200 to-cyan-200 bg-clip-text text-transparent">
                                        Event Registration Portal
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Right section - 100 Years Logo */}
                    <div className="flex items-center gap-3">
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative w-20 h-12 flex items-center justify-center bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                                <img
                                    src="/EMS/Images/Psg-100.png"
                                    alt="100 Years Celebration"
                                    className="w-14 h-14 object-contain"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Container */}
            <div className="relative px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="flex p-2 flex-col items-center justify-center border border-gray-100 rounded-xl shadow-lg hover:shadow-xl">
                        <div className="relative group">
                            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl blur-lg opacity-0  duration-300"></div>
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0">
                                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                                        <Calendar className="w-7 h-7 text-white" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Date & Time</span>
                                        <div className="px-2 py-1 bg-blue-50 rounded-md">
                                            <span className="text-xs font-bold text-blue-700">IST</span>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="font-bold text-gray-900 text-sm">
                                            {new Date(event.Start_datetime).toLocaleString('en-IN', {
                                                day: '2-digit',
                                                month: 'long',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col items-center justify-center text-center space-y-3bg-white border border-gray-100 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-200">
                        <div className="space-y-2 max-w-2xl">
                            <h1 className={`flex items-center gap-3 font-bold text-gray-900 leading-tight ${getEventNameFontSize(event.Event_Name)}`}>
                                {event.LogoUrl ? <img
                                    src={event.LogoUrl}
                                    alt="Event Logo"
                                    className="w-10 h-10 object-contain rounded-lg border-2 border-gray-700 flex-shrink-0"
                                /> : ""}
                                <span className="break-words text-center">
                                    {event.Event_Name?.toUpperCase()}
                                </span>
                            </h1>

                            <div className="flex flex-wrap items-center justify-center gap-2">
                                <div className="px-3 py-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full">
                                    <span className="text-xs font-bold text-white tracking-wider">{event.EventMode}</span>
                                </div>
                                {isGroupEvent && (
                                    <div className="px-3 py-1 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full">
                                        <span className="text-xs font-bold text-white tracking-wider">
                                            GROUP · MAX {maxMembers}
                                        </span>
                                    </div>
                                )}
                                {/* Sub-event indicator */}
                                {hasSubEvents && (
                                    <div className="px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full group relative cursor-help">
                                        <div className="flex items-center gap-1">
                                            <Layers className="w-3 h-3 text-white" />
                                            <span className="text-xs font-bold text-white tracking-wider">
                                                MULTI-EVENT
                                            </span>
                                        </div>
                                        {/* Tooltip */}
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                                            This event contains multiple sub-events
                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {event.Event_Tagline && (
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {event.Event_Tagline}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Organizer Contact */}
                    <div className="flex flex-col items-center lg:items-end justify-center">
                        <div className="relative group w-full max-w-xs">
                            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                            <div className="relative bg-white border border-gray-100 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-200">
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-lg flex items-center justify-center">
                                            <User className="w-5 h-5 text-blue-700" />
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1 mb-0.5">
                                            <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                                Event Coordinator
                                            </span>
                                            <Shield className="w-3 h-3 text-emerald-500" />
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-1.5">
                                            <div className="bg-blue-50 p-1.5 rounded-md">
                                                <User className="w-3.5 h-3.5 text-blue-600" />
                                            </div>
                                            <div className="text-sm font-extrabold text-gray-900 truncate">
                                                {event.organizer_name}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-1.5">
                                            <div className="bg-blue-50 p-1.5 rounded-md">
                                                <Phone className="w-3.5 h-3.5 text-blue-600" />
                                            </div>
                                            <div className="text-sm font-semibold text-gray-900">
                                                {event.Event_orgNum || "+91 422 257 2177"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Decorative Divider */}
                <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
                    </div>
                    <div className="relative flex justify-center">
                        <div className="px-6 bg-white">
                            <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
                        </div>
                    </div>
                </div>

                {/* Event Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Venue Card */}
                    <div className="group relative bg-white border border-gray-100 rounded-2xl p-6 shadow-lg hover:shadow-2xl hover:border-amber-200 transition-all duration-300 hover:-translate-y-1">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-amber-50/50 to-transparent rounded-full -translate-y-8 translate-x-8"></div>
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                                    <MapPin className="w-7 h-7 text-white" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Venue Location</span>
                                    <div className="px-2 py-1 bg-amber-50 rounded-md">
                                        <span className="text-xs font-bold text-amber-700">MAP</span>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <div className="font-bold text-gray-900 text-lg">{event.Venue_Location}</div>
                                    {event.Venue_Hall && (
                                        <div className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg">
                                            <div className="bg-white px-3 py-1 rounded-md">
                                                <span className="text-xs font-bold text-amber-700">HALL</span>
                                            </div>
                                            <span className="font-semibold text-gray-900">{event.Venue_Hall}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Availability Card */}
                    <div className="group relative bg-white border border-gray-100 rounded-2xl p-6 shadow-lg hover:shadow-2xl hover:border-emerald-200 transition-all duration-300 hover:-translate-y-1">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-emerald-50/50 to-transparent rounded-full -translate-y-8 translate-x-8"></div>
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
                                    <Users className="w-7 h-7 text-white" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">REGISTRATION STATUS</span>
                                    <div className="px-2 py-1 bg-emerald-50 rounded-md">
                                        <span className="text-xs font-bold text-emerald-700">LIVE</span>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                                            <span className="font-bold text-gray-900">REGISTRATION AVAILABLE</span>
                                        </div>
                                        <span className="text-xl font-bold text-emerald-700">
                                            {event.Seats_Available}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventHeader;